import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { alertsApi, type Alert as ApiAlert, type AlertFilters, type TrendDataPoint } from '../lib/alertsApi';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';
import { SnoozeDropdown } from '../components/alerts/SnoozeMenu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

type AlertType = 'budget' | 'performance' | 'demographic' | 'predictive';
type AlertStatus = 'active' | 'acknowledged' | 'resolved' | 'dismissed';

// Alert Trend Chart Component - PRD-3597
const AlertTrendChart: React.FC<{ organizationId: string; days: number }> = ({ organizationId, days }) => {
  const { data: trendData, isLoading, error } = useQuery({
    queryKey: ['alert-trend', organizationId, days],
    queryFn: () => alertsApi.getTrend(organizationId, days),
    refetchInterval: 60000, // 1 minute
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full text-red-500">
        Failed to load trend data
      </div>
    );
  }

  if (!trendData || trendData.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No trend data available
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={trendData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
        <XAxis 
          dataKey="date" 
          tick={{ fontSize: 12 }}
          tickFormatter={(value) => {
            const date = new Date(value);
            return `${date.getMonth() + 1}/${date.getDate()}`;
          }}
        />
        <YAxis tick={{ fontSize: 12 }} />
        <Tooltip
          contentStyle={{
            backgroundColor: 'var(--bg-color, white)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
          }}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="total" 
          stroke="#6b7280" 
          name="Total"
          strokeWidth={2}
          dot={false}
        />
        <Line 
          type="monotone" 
          dataKey="budget_burn" 
          stroke="#ef4444" 
          name="Budget Burn"
          strokeWidth={2}
          dot={false}
        />
        <Line 
          type="monotone" 
          dataKey="underperformance" 
          stroke="#f59e0b" 
          name="Underperformance"
          strokeWidth={2}
          dot={false}
        />
        <Line 
          type="monotone" 
          dataKey="demographic_shift" 
          stroke="#3b82f6" 
          name="Demographic"
          strokeWidth={2}
          dot={false}
        />
        <Line 
          type="monotone" 
          dataKey="opportunity" 
          stroke="#10b981" 
          name="Opportunity"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

// Map API alert to local format
const mapAlert = (apiAlert: ApiAlert): DisplayAlert => ({
  id: apiAlert.alert_id,
  type: apiAlert.alert_type as AlertType,
  status: apiAlert.status as AlertStatus,
  severity: apiAlert.severity as 'critical' | 'warning' | 'info',
  title: apiAlert.title,
  message: apiAlert.description,
  campaignName: apiAlert.campaign_name,
  campaignId: apiAlert.campaign_id,
  metric: apiAlert.benchmark?.toString() || 'N/A',
  threshold: apiAlert.benchmark?.toString() || 'N/A',
  actual: apiAlert.current_value?.toString() || 'N/A',
  firedAt: new Date(apiAlert.detected_at).toLocaleString(),
  acknowledgedAt: apiAlert.acknowledged_at ? new Date(apiAlert.acknowledged_at).toLocaleString() : undefined,
  acknowledgedBy: apiAlert.acknowledged_by,
  resolvedAt: apiAlert.resolved_at ? new Date(apiAlert.resolved_at).toLocaleString() : undefined,
  recommendations: apiAlert.recommendations,
});

interface DisplayAlert {
  id: string;
  type: AlertType;
  status: AlertStatus;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  message: string;
  campaignName: string;
  campaignId: string;
  metric: string;
  threshold: string;
  actual: string;
  firedAt: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  resolvedAt?: string;
  recommendations?: any[];
}

export const AlertHistoryPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<AlertType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<AlertStatus | 'all'>('active');
  const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set());
  const [showModal, setShowModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<DisplayAlert | null>(null);
  const [dateRange, setDateRange] = useState<'30' | '60' | '90'>('90');
  const [showTrendChart, setShowTrendChart] = useState(true);

  // Build API filters
  const apiFilters: AlertFilters = {
    ...(typeFilter !== 'all' && { alert_type: typeFilter }),
    ...(statusFilter !== 'all' && { status: statusFilter }),
  };

  // Fetch alerts with auto-refresh
  const { data: apiAlerts = [], isLoading, error } = useQuery({
    queryKey: ['alerts', apiFilters],
    queryFn: () => alertsApi.getAlerts(user?.organization_id || 'default-org', apiFilters),
    refetchInterval: 30000, // 30 seconds
    enabled: !!user?.organization_id,
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['alert-stats'],
    queryFn: () => alertsApi.getStats(user?.organization_id || 'default-org'),
    refetchInterval: 30000,
    enabled: !!user?.organization_id,
  });

  // Map API alerts to display format
  const alerts: DisplayAlert[] = apiAlerts.map(mapAlert);

  // Acknowledge mutation
  const acknowledgeMutation = useMutation({
    mutationFn: (alertId: string) => 
      alertsApi.acknowledge(alertId, user?.organization_id || 'default-org', user?.id || 'unknown'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  // Resolve mutation
  const resolveMutation = useMutation({
    mutationFn: ({ alertId, action }: { alertId: string; action: string }) =>
      alertsApi.resolve(alertId, user?.organization_id || 'default-org', user?.id || 'unknown', action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: ({ alertId, reason }: { alertId: string; reason: string }) =>
      alertsApi.dismiss(alertId, user?.organization_id || 'default-org', user?.id || 'unknown', reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  // Snooze mutation
  const snoozeMutation = useMutation({
    mutationFn: ({ alertId, durationHours }: { alertId: string; durationHours: number }) =>
      alertsApi.snooze(alertId, user?.organization_id || 'default-org', user?.id || 'unknown', durationHours),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alerts'] });
      queryClient.invalidateQueries({ queryKey: ['alert-stats'] });
    },
  });

  // Parse duration string to hours (e.g., "1h" -> 1, "4h" -> 4, "1d" -> 24, "1w" -> 168)
  const parseDurationToHours = (duration: string): number => {
    const match = duration.match(/^(\d+)([hdw])$/);
    if (!match) return 1; // default 1 hour
    const value = parseInt(match[1], 10);
    const unit = match[2];
    switch (unit) {
      case 'h': return value;
      case 'd': return value * 24;
      case 'w': return value * 24 * 7;
      default: return value;
    }
  };

  // Handle snooze
  const handleSnooze = async (alertId: string, duration: string) => {
    try {
      const durationHours = parseDurationToHours(duration);
      await snoozeMutation.mutateAsync({ alertId, durationHours });
    } catch (err) {
      console.error('Snooze failed:', err);
    }
  };

  const filteredAlerts = alerts;

  const handleSelectAll = () => {
    if (selectedAlerts.size === filteredAlerts.length) {
      setSelectedAlerts(new Set());
    } else {
      setSelectedAlerts(new Set(filteredAlerts.map(a => a.id)));
    }
  };

  const handleSelectAlert = (id: string) => {
    const newSelected = new Set(selectedAlerts);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedAlerts(newSelected);
  };

  const handleBulkAction = async (action: 'acknowledge' | 'dismiss' | 'resolve') => {
    const alertIds = Array.from(selectedAlerts);
    
    try {
      for (const alertId of alertIds) {
        if (action === 'acknowledge') {
          await acknowledgeMutation.mutateAsync(alertId);
        } else if (action === 'resolve') {
          await resolveMutation.mutateAsync({ alertId, action: 'Bulk resolved by user' });
        } else if (action === 'dismiss') {
          await dismissMutation.mutateAsync({ alertId, reason: 'Bulk dismissed by user' });
        }
      }
      setSelectedAlerts(new Set());
    } catch (err) {
      console.error(`Bulk ${action} failed:`, err);
    }
  };

  const handleViewDetails = (alert: DisplayAlert) => {
    setSelectedAlert(alert);
    setShowModal(true);
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await acknowledgeMutation.mutateAsync(alertId);
      setShowModal(false);
    } catch (err) {
      console.error('Acknowledge failed:', err);
    }
  };

  const handleResolve = async (alertId: string) => {
    try {
      await resolveMutation.mutateAsync({ alertId, action: 'Resolved by user from modal' });
      setShowModal(false);
    } catch (err) {
      console.error('Resolve failed:', err);
    }
  };

  const handleDismiss = async (alertId: string) => {
    try {
      await dismissMutation.mutateAsync({ alertId, reason: 'Dismissed by user from modal' });
      setShowModal(false);
    } catch (err) {
      console.error('Dismiss failed:', err);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 dark:bg-red-900/20';
      case 'warning': return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20';
      case 'info': return 'text-blue-600 bg-blue-50 dark:bg-blue-900/20';
      default: return 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
    }
  };

  const getTypeIcon = (type: AlertType) => {
    switch (type) {
      case 'budget':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />;
      case 'performance':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />;
      case 'demographic':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />;
      case 'predictive':
        return <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />;
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300 font-medium">Error loading alerts</p>
          <p className="text-red-600 dark:text-red-400 text-sm mt-1">
            {error instanceof Error ? error.message : 'Unknown error occurred'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Alert History</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            View and manage all campaign alerts {isLoading && '(Loading...)'}
          </p>
        </div>
        {selectedAlerts.size > 0 && (
          <div className="flex gap-2">
            <button
              onClick={() => handleBulkAction('acknowledge')}
              disabled={acknowledgeMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Acknowledge ({selectedAlerts.size})
            </button>
            <button
              onClick={() => handleBulkAction('resolve')}
              disabled={resolveMutation.isPending}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              Resolve ({selectedAlerts.size})
            </button>
            <button
              onClick={() => handleBulkAction('dismiss')}
              disabled={dismissMutation.isPending}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50"
            >
              Dismiss ({selectedAlerts.size})
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Alert Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as AlertType | 'all')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Types</option>
              <option value="budget">Budget</option>
              <option value="performance">Performance</option>
              <option value="demographic">Demographic</option>
              <option value="predictive">Predictive</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AlertStatus | 'all')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Date Range & Export Actions - PRD-3597 */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Date Range:</span>
            <div className="flex gap-2">
              {(['30', '60', '90'] as const).map((days) => (
                <button
                  key={days}
                  onClick={() => setDateRange(days)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    dateRange === days
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  data-testid={`date-filter-${days}`}
                >
                  {days} Days
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowTrendChart(!showTrendChart)}
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {showTrendChart ? 'Hide' : 'Show'} Trend
            </button>
            <button
              onClick={() => alertsApi.exportCSV(user?.organization_id || 'default-org', { days: parseInt(dateRange) })}
              className="px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV ({dateRange} days)
            </button>
          </div>
        </div>
      </div>

      {/* Alert Trend Chart - PRD-3597 */}
      {showTrendChart && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Alert Trend (Last {dateRange} Days)</h3>
          <div className="h-64">
            <AlertTrendChart organizationId={user?.organization_id || 'default-org'} days={parseInt(dateRange)} />
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total Alerts</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
            {stats?.total_alerts || alerts.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Active</p>
          <p className="text-2xl font-bold text-red-600 mt-1">
            {stats?.active_alerts || alerts.filter(a => a.status === 'active').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Acknowledged</p>
          <p className="text-2xl font-bold text-yellow-600 mt-1">
            {alerts.filter(a => a.status === 'acknowledged').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">Resolved</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {alerts.filter(a => a.status === 'resolved').length}
          </p>
        </div>
      </div>

      {/* Alerts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredAlerts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400 text-lg">No alerts found</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
              Try changing your filters or check back later
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900">
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedAlerts.size === filteredAlerts.length && filteredAlerts.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 dark:border-gray-600"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Alert
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Fired At
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAlerts.map((alert) => (
                  <tr key={alert.id} className="hover:bg-gray-50 dark:hover:bg-gray-700" data-testid="alert-row">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedAlerts.has(alert.id)}
                        onChange={() => handleSelectAlert(alert.id)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(alert.severity)}`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {getTypeIcon(alert.type)}
                        </svg>
                        <span className="text-sm text-gray-900 dark:text-white capitalize">{alert.type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{alert.title}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{alert.message}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        className="text-sm text-gp-sungold hover:text-gp-sungold-600"
                        onClick={() => window.location.href = `/campaigns/${alert.campaignId}`}
                      >
                        {alert.campaignName}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={alert.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {alert.firedAt}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewDetails(alert)}
                          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                          data-testid="view-details-btn"
                        >
                          View
                        </button>
                        {alert.status === 'active' && (
                          <SnoozeDropdown
                            alertId={alert.id}
                            organizationId={user?.organization_id || 'default-org'}
                            userId={user?.id || 'unknown'}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="alert-detail-modal">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedAlert.title}</h2>
                  <p className="text-gray-600 dark:text-gray-400 mt-1">{selectedAlert.message}</p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="close-modal-btn"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Campaign</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedAlert.campaignName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Type</p>
                  <p className="font-medium text-gray-900 dark:text-white capitalize">{selectedAlert.type}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Metric</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedAlert.metric}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Severity</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(selectedAlert.severity)}`}>
                    {selectedAlert.severity}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Threshold</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedAlert.threshold}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Actual Value</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedAlert.actual}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Fired At</p>
                  <p className="font-medium text-gray-900 dark:text-white">{selectedAlert.firedAt}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                  <StatusBadge status={selectedAlert.status} />
                </div>
              </div>

              {/* Recommendations */}
              {selectedAlert.recommendations && selectedAlert.recommendations.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Recommendations</p>
                  <div className="space-y-2">
                    {selectedAlert.recommendations.map((rec: any, idx: number) => (
                      <div key={idx} className="bg-white dark:bg-gray-800 p-3 rounded">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{rec.title}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{rec.description}</p>
                        {rec.expected_impact && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Expected Impact: {rec.expected_impact}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedAlert.acknowledgedAt && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-300">Acknowledged</p>
                  <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                    By {selectedAlert.acknowledgedBy} at {selectedAlert.acknowledgedAt}
                  </p>
                </div>
              )}

              {selectedAlert.resolvedAt && (
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                  <p className="text-sm font-medium text-green-900 dark:text-green-300">Resolved</p>
                  <p className="text-sm text-green-700 dark:text-green-400 mt-1">
                    At {selectedAlert.resolvedAt}
                  </p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              {selectedAlert.status === 'active' && (
                <>
                  <button 
                    onClick={() => handleAcknowledge(selectedAlert.id)}
                    disabled={acknowledgeMutation.isPending}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    data-testid="acknowledge-btn"
                  >
                    Acknowledge
                  </button>
                  <button 
                    onClick={() => handleResolve(selectedAlert.id)}
                    disabled={resolveMutation.isPending}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                    data-testid="resolve-btn"
                  >
                    Resolve
                  </button>
                </>
              )}
              {selectedAlert.status === 'acknowledged' && (
                <button 
                  onClick={() => handleResolve(selectedAlert.id)}
                  disabled={resolveMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Resolve
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertHistoryPage;
